import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as ShCalendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Star, MapPin, Calendar as CalendarIcon, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";

const TIMES = [];
for (let h = 11; h <= 22; h++) {
    TIMES.push(`${h.toString().padStart(2, "0")}:00`);
    TIMES.push(`${h.toString().padStart(2, "0")}:30`);
}

export default function RestaurantDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [r, setR] = useState(null);
    const [menu, setMenu] = useState([]);
    const [loading, setLoading] = useState(true);

    const [date, setDate] = useState(new Date());
    const [time, setTime] = useState("19:00");
    const [party, setParty] = useState(2);
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const [rr, mm] = await Promise.all([
                    api.get(`/restaurants/${id}`),
                    api.get(`/restaurants/${id}/menu`),
                ]);
                setR(rr.data);
                setMenu(mm.data || []);
            } catch (error) {
                console.warn("[restaurant-detail] load failed", error?.message);
                toast.error("Unable to load restaurant");
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    const categories = useMemo(() => {
        const map = {};
        for (const m of menu) {
            map[m.category] = map[m.category] || [];
            map[m.category].push(m);
        }
        return map;
    }, [menu]);

    const submit = async () => {
        const dt = new Date(date);
        const [hh, mm] = time.split(":").map(Number);
        dt.setHours(hh, mm, 0, 0);
        setSubmitting(true);
        try {
            const { data } = await api.post("/reservations", {
                restaurant_id: id,
                party_size: party,
                reservation_time: dt.toISOString(),
                notes,
            });
            toast.success("Reservation confirmed!");
            navigate("/reservations");
        } catch (error) {
            console.warn("[restaurant-detail] submit reservation failed", error?.message);
            toast.error(error?.response?.data?.detail || "Could not book reservation");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
                <Skeleton className="h-80 w-full mb-6" />
                <div className="grid lg:grid-cols-[1fr_400px] gap-8">
                    <Skeleton className="h-96" />
                    <Skeleton className="h-96" />
                </div>
            </div>
        );
    }

    if (!r) return null;

    return (
        <div>
            <div className="relative">
                <AspectRatio ratio={32 / 11}>
                    <img src={r.image_url} alt={r.name} className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                </AspectRatio>
                <div className="absolute bottom-0 left-0 right-0">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 text-white">
                        <div className="flex flex-wrap gap-2 mb-2">
                            <Badge className="bg-[hsl(var(--brand-gold)/0.95)] text-[hsl(var(--brand-ink))] border-0">
                                <Star className="h-3 w-3 mr-1 fill-current" /> {r.rating?.toFixed?.(1) || r.rating}
                            </Badge>
                            <Badge variant="secondary" className="bg-white/80 text-foreground border-0">{r.price_range}</Badge>
                            <Badge variant="secondary" className="bg-white/80 text-foreground border-0">{r.cuisine}</Badge>
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl" data-testid="restaurant-title">{r.name}</h1>
                        <div className="flex items-center gap-2 text-white/85 mt-1 text-sm">
                            <MapPin className="h-4 w-4" /> {r.address}
                        </div>
                    </div>
                </div>
            </div>

            <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 grid lg:grid-cols-[1fr_420px] gap-8 items-start">
                <div className="space-y-8">
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="font-display text-2xl mb-2">About</h2>
                            <p className="text-muted-foreground">{r.description}</p>
                            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                                <div><div className="text-muted-foreground">Open</div><div className="font-medium">{r.opens_at} – {r.closes_at}</div></div>
                                <div><div className="text-muted-foreground">Cuisine</div><div className="font-medium">{r.cuisine}</div></div>
                                <div><div className="text-muted-foreground">Price</div><div className="font-medium">{r.price_range}</div></div>
                                <div><div className="text-muted-foreground">Reviews</div><div className="font-medium">{r.review_count}</div></div>
                            </div>
                        </CardContent>
                    </Card>

                    <div>
                        <h2 className="font-display text-2xl mb-3">Menu preview</h2>
                        <div className="space-y-6">
                            {Object.entries(categories).map(([cat, items]) => (
                                <div key={cat}>
                                    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">{cat}</div>
                                    <div className="grid sm:grid-cols-2 gap-3">
                                        {items.slice(0, 6).map(it => (
                                            <Card key={it.id} className="overflow-hidden">
                                                <div className="flex">
                                                    {it.image_url && (
                                                        <img src={it.image_url} alt={it.name} className="h-24 w-28 object-cover" />
                                                    )}
                                                    <div className="p-3 flex-1">
                                                        <div className="flex items-center justify-between">
                                                            <div className="font-medium leading-tight">{it.name}</div>
                                                            <div className="text-sm font-semibold">${it.price.toFixed(2)}</div>
                                                        </div>
                                                        <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{it.description}</div>
                                                    </div>
                                                </div>
                                            </Card>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <Card className="sticky top-24" data-testid="reservation-widget">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-1">
                            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                            <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Reserve a table</span>
                        </div>
                        <h3 className="font-display text-2xl mb-4">Tonight in {r.cuisine}</h3>
                        <div className="space-y-3">
                            <div className="grid gap-1.5">
                                <Label>Date</Label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" className="w-full justify-start" data-testid="reservation-date-button">
                                            <CalendarIcon className="h-4 w-4 mr-2" />
                                            {formatDate(date.toISOString())}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent align="start" className="p-0">
                                        <ShCalendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} initialFocus />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="grid gap-1.5">
                                    <Label>Time</Label>
                                    <Select value={time} onValueChange={setTime}>
                                        <SelectTrigger data-testid="reservation-time-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {TIMES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid gap-1.5">
                                    <Label>Party size</Label>
                                    <Select value={party.toString()} onValueChange={(v) => setParty(parseInt(v, 10))}>
                                        <SelectTrigger data-testid="reservation-party-size-select">
                                            <Users className="h-3.5 w-3.5 mr-1" /><SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Array.from({ length: 12 }, (_, i) => i + 1).map(n =>
                                                <SelectItem key={n} value={n.toString()}>{n} {n === 1 ? "guest" : "guests"}</SelectItem>
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid gap-1.5">
                                <Label>Special requests (optional)</Label>
                                <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. window seat, anniversary" data-testid="reservation-notes" />
                            </div>
                            <Button className="w-full h-11" onClick={submit} disabled={submitting} data-testid="reservation-submit-button">
                                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                Confirm reservation
                            </Button>
                            <p className="text-[11px] text-muted-foreground text-center">You can modify or cancel up to 1 hour before.</p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
